import crossIcon from '@xero/xui-icon/icons/cross';
import XUIButton from '@xero/xui/react/button';
import XUIIcon from '@xero/xui/react/icon';
import { XUIPickitem } from '@xero/xui/react/picklist';
import Fuse from 'fuse.js';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DropDownSelect } from '../../../../../components/DropDownSelect';
import { IAccount } from '../../../../../models';
import { IShopifySettingsResponse } from '../../../../../models/shopify';
import { clearingAccountRemoved, paymentGatewayChanged } from '../../../../../redux/slices/AccountSetupSlice';
import { settingsSelectors } from '../../../../../redux/slices/SettingsSlice';
import { deepClone, useAppDispatch } from '../../../../../redux/store';
import { IBankAccountDropdownProps } from './BankAccountDropdown';
import '../../PaymentGatewaysSetupTab/PaymentGatewaysSetupTab.css';
import { formatOption, isClearingAccountMissing } from './utils';

export interface IClearingAccountDropdownProps
    extends Omit<IBankAccountDropdownProps, 'reconcilable' | 'paymentGatewayBankAccountDeletedState'> {
    onClearingAccountCreate: (paymentGateway: string) => void;
    setSelectPGCellInvalid: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ClearingAccountDropdown: FC<IClearingAccountDropdownProps> = ({
    currentTableData,
    setCurrentTableData,
    dataRow,
    lang,
    onGatewayChange,
    onClearingAccountCreate,
    setSelectPGCellInvalid,
    invalidRowsState,
}) => {
    const dispatch = useAppDispatch();

    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const bankAccounts = settings.bankAccounts;

    const [searchString, setSearchString] = useState('');
    const [invalidRows, setInvalidRows] = invalidRowsState;

    const handleFooterOnClick = () => onClearingAccountCreate(dataRow.paymentType);
    const handleClearingAccountSelection = (account: IAccount) => () => {
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
        if (paymentGatewayDetails) {
            paymentGatewayDetails[dataRow.paymentType].clearingAccountId = account.accountId;
            paymentGatewayDetails[dataRow.paymentType].clearingAccountCurrency = account.currencyCode;
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === dataRow.paymentType
            );
            currentTableData[index].bankAccountId = account.accountId;
            const invalidRowIndex = invalidRows.findIndex((invalidRow) => invalidRow === dataRow.paymentType);
            if (invalidRowIndex !== -1) {
                invalidRows.splice(invalidRowIndex, 1);
                setInvalidRows([...invalidRows]);
            }
            setCurrentTableData(currentTableData);
            onGatewayChange(paymentGatewayDetails);
            dispatch(
                paymentGatewayChanged(dataRow.paymentType, {
                    ...paymentGatewayDetails[dataRow.paymentType],
                    clearingAccountId: account.accountId,
                    clearingAccountCurrency: account.currencyCode,
                })
            );
            setSearchString('');
            if (dataRow.paymentType === lang.ROW_SELECT_PAYMENT_TYPE) {
                setSelectPGCellInvalid(true);
            }
        }
    };

    const handleDeselectClearingAccountClick = () => {
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
        if (paymentGatewayDetails && paymentGatewayDetails[dataRow.paymentType].clearingAccountId) {
            delete paymentGatewayDetails[dataRow.paymentType].clearingAccountId;
            delete paymentGatewayDetails[dataRow.paymentType].clearingAccountCurrency;
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === dataRow.paymentType
            );
            delete currentTableData[index].clearingAccountId;
            if (!!dataRow.bankAccount && invalidRows.indexOf(dataRow.paymentType) === -1) {
                setInvalidRows([...invalidRows, dataRow.paymentType]);
            }
            setCurrentTableData(currentTableData);
            onGatewayChange(paymentGatewayDetails);
            dispatch(clearingAccountRemoved(dataRow.paymentType));
            setSelectPGCellInvalid(false);
        }
    };
    const { clearingAccount, createInvoices } = dataRow;
    const validAccounts = useMemo(
        () => bankAccounts
            ?.filter(
                (f) =>
                    settings.paymentGatewayDetails &&
                    !Object.values(settings.paymentGatewayDetails).some((s) => s.bankAccountId === f.accountId) &&
                    (!dataRow.bankAccount || dataRow.bankAccount.currencyCode === f.currencyCode)
            )
            .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)) ?? [], 
        [bankAccounts, dataRow.bankAccount, settings.paymentGatewayDetails]);

    const handleInputChanges = (event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value);

    const fuse = useMemo(
        () =>
            validAccounts
                ? new Fuse(validAccounts, {
                      keys: ['name'],
                  })
                : null,
        [validAccounts]
    );

    const fuzzySearchResult = fuse?.search(searchString).map((m) => m.item);

    const dropDownAccounts = fuzzySearchResult && fuzzySearchResult.length > 0 ? fuzzySearchResult : validAccounts;

    const clearingAccountSelect = (
        <DropDownSelect
            buttonContent={clearingAccount?.name}
            showFooter={true}
            footerActionText='Create clearing account'
            footerActionOnClick={handleFooterOnClick}
            hideCaret={!!clearingAccount}
            showSearchBox={true}
            onInputChange={handleInputChanges}
            onBlur={() => setSearchString('')}
            isInvalid={isClearingAccountMissing(dataRow.clearingAccount, dataRow.bankAccount)}
            validationMessage={lang.CLEARING_ACCOUNT_REQUIRED}
            hideInvalidIcon={true}
        >
            {dropDownAccounts.map((m, i) => (
                <XUIPickitem
                    key={i}
                    id={`clearing-account-item-${i}`}
                    onClick={handleClearingAccountSelection(m)}
                    isSelected={clearingAccount?.accountId === m.accountId}
                >
                    {formatOption(m.name, m.bankAccountNumber)}
                </XUIPickitem>
            ))}
        </DropDownSelect>
    );

    if (createInvoices) {
        return (
            <div
                className={
                    isClearingAccountMissing(dataRow.clearingAccount, dataRow.bankAccount) ? 'validation-border' : ''
                }
            >
                {clearingAccount ? (
                    <div className='deselect-container'>
                        {clearingAccountSelect}
                        <XUIButton
                            className='deselect-button'
                            onClick={handleDeselectClearingAccountClick}
                            variant='borderless-standard'
                            qaHook='clearing-account-deselect-button'
                        >
                            <XUIIcon
                                icon={crossIcon}
                                color='black-faint'
                                className='xui-margin-top-2xsmall clear-account-icon'
                            />
                        </XUIButton>
                    </div>
                ) : (
                    clearingAccountSelect
                )}
            </div>
        );
    } else {
        return <></>;
    }
};
